import React, { useState } from 'react';
import { Badge, Card } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { subtleBadgeProps } from '@core/utils/subtleBadge';
import PreviewNameList from '@core/reports/components/PreviewNameList';
import type {
  DashboardChecklistDepartmentItem,
  DashboardReportingChecklistPeriod,
} from '../types/dashboard';
import {
  activitySubmitPath,
  departmentProgressPath,
} from '../utils/dashboardPaths';
import { buildActivityEntrySubmitSearch } from '@features/report/activity-entry/constants/activityEntryQueryParams';
import {
  formatDeadlineCountdown,
  formatPeriodRangeLabel,
  formatReportTagDeadline,
  periodChecklistHasOutstanding,
} from '@features/report/tag/utils/reportTagDeadline';

interface Props {
  periods: DashboardReportingChecklistPeriod[];
  scope: string;
  scopeId: number;
  /** When false, outstanding templates show status only (no Open → submit). */
  canSaveActivity?: boolean;
}

function urgencyBadgeVariant(
  urgency?: string | null
): 'success' | 'warning' | 'danger' | 'info' | 'secondary' {
  if (urgency === 'danger') return 'danger';
  if (urgency === 'warning') return 'warning';
  if (urgency === 'info') return 'info';
  return 'secondary';
}

function childrenNoun(scope: string): string {
  const upper = scope.toUpperCase();
  if (upper === 'NATION') return 'districts';
  if (upper === 'DISTRICT') return 'churches';
  return 'children';
}

/** Ready / blocked hint for outstanding office department reports. */
export function departmentChecklistReadinessLabel(
  item: DashboardChecklistDepartmentItem,
  scope: string
): string | null {
  if (item.is_approved || item.readiness === 'complete') {
    return null;
  }
  const outstanding = item.children_outstanding ?? 0;
  if (item.readiness === 'blocked' || outstanding > 0) {
    const noun = childrenNoun(scope);
    return `Blocked · ${outstanding} ${noun} outstanding`;
  }
  if (item.readiness === 'ready' || item.readiness == null) {
    return 'Ready to complete';
  }
  return null;
}

const ReportingChecklistPanel: React.FC<Props> = ({
  periods,
  scope,
  scopeId,
  canSaveActivity = true,
}) => {
  const [expandedId, setExpandedId] = useState<number | null>(
    periods[0]?.report_tag_id ?? null
  );

  if (!periods.length) return null;

  return (
    <Card id="dashboard-reporting-checklist" className="ntcog-card dashboard-section-card mb-4">
      <Card.Header>
        <div>My reporting checklist</div>
        <small className="text-muted fw-normal d-block">
          Open periods only. Office templates count when submitted. Department reports expected
          for this workplace (including rollup from churches/districts below) count when approved.
        </small>
      </Card.Header>
      <Card.Body className="p-0">
        <div className="dashboard-progress-list">
          {periods.map((period) => {
            const isExpanded = expandedId === period.report_tag_id;
            const templateTotal =
              period.templates.submitted + period.templates.outstanding;
            const deptTotal =
              period.department_reports.approved + period.department_reports.outstanding;
            const periodLabel = formatPeriodRangeLabel(period.since, period.until);
            const deadlineLabel = formatReportTagDeadline(period.deadline);
            const hasOutstanding = periodChecklistHasOutstanding(period);
            const countdown = hasOutstanding
              ? formatDeadlineCountdown(period.deadline)
              : null;
            const badgeLabel = hasOutstanding
              ? countdown ?? (deadlineLabel ? 'Deadline set' : 'Open period')
              : templateTotal + deptTotal > 0
                ? 'Complete'
                : deadlineLabel
                  ? 'Deadline set'
                  : 'Open period';
            const badgeVariant = hasOutstanding
              ? urgencyBadgeVariant(period.deadline_urgency)
              : 'success';

            const templateSummary = `${period.templates.submitted} of ${templateTotal} templates submitted`;
            const deptSummary =
              period.department_reports.outstanding === 0 && deptTotal > 0
                ? 'All expected department reports approved'
                : `${period.department_reports.approved} of ${deptTotal} department reports approved`;

            const templatePreviewItems = period.templates.items.map((item) => ({
              id: `${item.department_id}-${item.id}`,
              name: item.name,
              item,
            }));
            const deptPreviewItems = period.department_reports.items.map((item) => ({
              id: item.department_id,
              name: item.department_name,
              item,
            }));

            return (
              <div key={period.report_tag_id} className="dashboard-progress-period">
                <button
                  type="button"
                  className="dashboard-progress-period__toggle w-100 border-0 bg-transparent text-start"
                  onClick={() =>
                    setExpandedId(isExpanded ? null : period.report_tag_id)
                  }
                  aria-expanded={isExpanded}
                >
                  <div className="dashboard-progress-period__row d-flex align-items-center justify-content-between gap-3">
                    <div className="min-w-0">
                      <div className="fw-semibold">{period.period_name}</div>
                      <small className="text-muted d-block">
                        {templateSummary}
                        {deptTotal > 0 ? ` · ${deptSummary}` : null}
                      </small>
                      {(periodLabel || deadlineLabel) && (
                        <small className="text-muted d-block">
                          {periodLabel ? `Period: ${periodLabel}` : null}
                          {periodLabel && deadlineLabel ? ' · ' : null}
                          {deadlineLabel ? `Deadline: ${deadlineLabel}` : null}
                        </small>
                      )}
                    </div>
                    <div className="d-flex align-items-center gap-2 flex-shrink-0">
                      <Badge {...subtleBadgeProps(badgeVariant)}>
                        {badgeLabel}
                      </Badge>
                      {isExpanded ? <FiChevronUp /> : <FiChevronDown />}
                    </div>
                  </div>
                </button>

                {isExpanded && (
                  <div className="dashboard-progress-period__details px-3 pb-3">
                    <div className="mb-3">
                      <div className="fw-semibold text-muted small mb-2">Templates</div>
                      {period.templates.items.length === 0 ? (
                        <div className="small text-muted">No required templates for this period.</div>
                      ) : (
                        <PreviewNameList
                          items={templatePreviewItems}
                          emptyLabel="No required templates for this period."
                          renderItem={(row) => {
                            const item = row.item;
                            const search = buildActivityEntrySubmitSearch({
                              reportTemplateId: item.id,
                              reportTagId: period.report_tag_id,
                              departmentId: item.department_id,
                            });
                            const path = `${activitySubmitPath(scope, scopeId)}?${search.toString()}`;
                            return (
                              <li
                                className="d-flex justify-content-between align-items-center gap-2 py-1 border-bottom border-secondary-subtle"
                              >
                                <span>
                                  {item.is_submitted ? '✓' : '○'} {item.name}
                                </span>
                                <span className="text-muted flex-shrink-0">
                                  {item.is_submitted ? (
                                    'Submitted'
                                  ) : canSaveActivity ? (
                                    <Link to={path} className="text-decoration-none">
                                      Open
                                    </Link>
                                  ) : (
                                    'Outstanding'
                                  )}
                                </span>
                              </li>
                            );
                          }}
                        />
                      )}
                    </div>

                    <div>
                      <div className="fw-semibold text-muted small mb-2">Department reports</div>
                      {period.department_reports.items.length === 0 ? (
                        <div className="small text-muted">
                          No expected department reports for this period.
                        </div>
                      ) : (
                        <PreviewNameList
                          items={deptPreviewItems}
                          emptyLabel="No expected department reports for this period."
                          renderItem={(row) => {
                            const item = row.item;
                            const path = departmentProgressPath(
                              scope,
                              scopeId,
                              period.report_tag_id,
                              item.department_id
                            );
                            const readinessLabel = departmentChecklistReadinessLabel(item, scope);
                            return (
                              <li
                                className="d-flex justify-content-between align-items-start gap-2 py-1 border-bottom border-secondary-subtle"
                              >
                                <span className="min-w-0">
                                  <span className="d-block">
                                    {item.is_approved ? '✓' : '○'} {item.department_name}
                                    <span className="text-muted"> · {item.status_label}</span>
                                  </span>
                                  {readinessLabel ? (
                                    <span className="d-block small text-muted">{readinessLabel}</span>
                                  ) : null}
                                </span>
                                <Link to={path} className="text-decoration-none flex-shrink-0">
                                  Open report
                                </Link>
                              </li>
                            );
                          }}
                        />
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Card.Body>
    </Card>
  );
};

export default ReportingChecklistPanel;
